/* eslint-disable max-lines -- cohesive set of custom-field cell renderers; kept in one module by design */
/**
 * Custom Field Cell Renderers
 *
 * Inline cell renderer functions for each CustomFieldType.
 * Each renderer returns a `cell` function compatible with TanStack ColumnDef,
 * keeping all custom-field rendering logic self-contained within the customFields module.
 *
 * Rendering logic is adapted from the shared fieldTypes renderers
 * (tagsDropdown.tsx, multiselect.tsx, text.tsx, number.tsx, date.tsx).
 */

import React, { useState, useCallback } from 'react';
import { flexRender } from '@tanstack/react-table';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { ComboBoxOption } from 'components/inputs/types';
import styled from '@emotion/styled';
import { format } from 'date-fns';
import { t } from '@lingui/macro';
import { getDateFromValue } from 'components/inputs/DatePicker';

import {
  CellContainer,
  TagsReadOnlyContainer,
  TagsContainer,
  Tag,
  InvalidTag,
  TruncatedText,
} from '../../tableStyles';
import { TagsWithOverflow } from 'components/tags/TagsWithOverflow';
import { TableTooltip } from '../../TableTooltip';
import { useEditableContextOptional } from '../../OptimizedTableProvider';
import {
  EditableDropdownCell,
  ReadOnlyDropdownCell,
  EditableTextCell,
  EditableNumberCell,
  EditableMultiselectCell,
  DatePickerCell,
} from '../../columnTypes/inline';
import type { EditableMultiselectCellProps } from '../../columnTypes/inline';
import { usePropDrivenInlineEditSync } from '../../columnTypes/inline/usePropDrivenInlineEditSync';
import { getCellValue, parseMultiSelectValue } from '../../columnTypes/cellValue';
import type { CustomFieldEntry } from '../types';
import { parseCustomFieldColumnId } from '../types';
import type { HierarchyChildBulkEditEntity } from '../validation/hierarchy';
import { useDialog } from 'features/modalManager';
import { Tooltip } from 'components';

// ============================================================================
// Internal Types
// ============================================================================

type OnUpdate<T> = (row: T, updates: Partial<T>) => void | Promise<void>;

/** Option with optional parentId in customProps (for NestedAllowedValue) */
type OptionWithParent = {
  id: string | number;
  label: string;
  customProps?: { parentId?: number | null };
};

// ============================================================================
// Shared Helpers
// ============================================================================

/**
 * Get display string from a cell context value (string, null, object with label/name).
 */
const getStringValue = <T extends { id: number | string }>(
  context: CellContext<T, unknown>
): string => {
  const value = getCellValue(context, context.column.id);
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    return value
      .map(v =>
        typeof v === 'object' && v !== null
          ? (v as { label?: string; name?: string }).label ?? (v as { name?: string }).name ?? ''
          : String(v)
      )
      .join(', ');
  }
  if (typeof value === 'object') {
    return String(
      (value as { name?: string; label?: string }).name ?? (value as { label?: string }).label ?? ''
    );
  }
  return String(value);
};

/**
 * Check if a value exists in the allowed options (by label or id).
 */
const isValueValid = (
  cellValue: string,
  options?: Array<{ id: string | number; label: string }>
): boolean => {
  if (!cellValue) return true;
  if (!options || options.length === 0) return true;
  return options.some(opt => opt.label === cellValue || String(opt.id) === cellValue);
};

/**
 * Error message for a selected value that no longer matches an active allowed
 * option (e.g. the option was deactivated after being selected). Shared by the
 * single-select and nested allowed-value inline dropdowns.
 */
const getInvalidAllowedValueError = (cellValue: string, isValid: boolean): string | undefined =>
  cellValue && !isValid ? t`Please select valid and active allowed value.` : undefined;

/**
 * Whether a single selected multiselect entry still matches one of the field's
 * active allowed options (by id or label).
 */
const isMultiselectEntryValid = (
  entry: { id: string | number; label: string },
  options?: Array<{ id: string | number; label: string }>
): boolean => {
  if (!options || options.length === 0) return true;
  return options.some(opt => opt.label === entry.label || String(opt.id) === String(entry.id));
};

/**
 * Same check as `getInvalidAllowedValueError`, applied to every currently-selected
 * value in a multiselect custom field. Flags the cell as invalid when any selected
 * tag no longer matches one of the field's active allowed options.
 */
const getInvalidMultiselectError = (
  values: Array<{ id: string | number; label: string }>,
  options?: Array<{ id: string | number; label: string }>
): string | undefined =>
  values.some(v => !isMultiselectEntryValid(v, options))
    ? t`Please select valid and active allowed value.`
    : undefined;

/**
 * Filter options for hierarchical (NestedAllowedValue) fields.
 * - No selection: show only top-level options (parentId null/0/undefined).
 * - Selection: show children of the selected value; fall back to top-level if none exist.
 */
const filterOptionsForNested = (
  options: OptionWithParent[] | undefined,
  currentValueId: string | number | null | undefined
): OptionWithParent[] => {
  if (!options || options.length === 0) return [];
  const hasHierarchy = options.some(opt => opt.customProps?.parentId !== undefined);
  if (!hasHierarchy) return options;

  const isParentOption = (opt: OptionWithParent): boolean => {
    const p = opt.customProps?.parentId;
    return p === null || p === undefined || p === 0;
  };

  if (!currentValueId) return options.filter(isParentOption);

  const children = options.filter(opt => opt.customProps?.parentId === currentValueId);
  if (children.length > 0) return children;
  const parents = options.filter(isParentOption);
  return parents.length > 0 ? parents : options;
};

/**
 * Mandatory-for-export inline validation message, worded per entity (dl-60606).
 * Styles and Items share these cells, so the entity is threaded down from
 * generateCustomFieldColumns (`bulkEditEntity`) and resolved here at render time
 * so LinguiJS locale switching stays correct.
 */
export const getMandatoryForExportMessage = (entity?: HierarchyChildBulkEditEntity): string =>
  entity === 'style'
    ? t`This field is mandatory for Ready for Export Styles`
    : t`This field is mandatory for Ready for Export items`;

const defaultDateFormatter = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'dd-MMM-yyyy');
  } catch {
    return t`Invalid date`;
  }
};

// ============================================================================
// Scoped custom fields (dl-58878)
// ============================================================================

/** Muted, italic text used for the "Not applicable" scoped-field state. */
const MutedText = styled.span`
  color: ${p => p.theme.palette.text.disabled};
  font-style: italic;
`;

/**
 * Whether a custom field applies to a row, read from the row's custom field entries.
 * Defaults to true when the entry is absent (unscoped fields, fields without a value,
 * or entities that have no scope concept such as Items).
 */
const getRowCustomFieldApplies = (
  row: { id: number | string; customFields?: CustomFieldEntry[] },
  customFieldId: number
): boolean => {
  const entry = row.customFields?.find(cf => cf.customFieldId === customFieldId);
  return entry?.appliesToStyle ?? true;
};

/**
 * Wraps a base cell renderer so that custom fields scoped to a brand/group/category
 * render "Not applicable" (with an explanatory tooltip) for rows the field does not
 * apply to. Applicable rows delegate to the base renderer unchanged — including its
 * inline-edit behaviour — so a non-applicable cell is never editable.
 *
 * The base renderer is rendered as a child component (via createElement) rather than
 * called directly, so its hooks run in their own render and hook order is preserved.
 */
export const makeScopedCell = <T extends { id: number | string }>(
  customFieldId: number,
  baseCell: NonNullable<ColumnDef<T>['cell']>
): NonNullable<ColumnDef<T>['cell']> =>
  function ScopedCell(context: CellContext<T, unknown>): React.ReactNode {
    if (!getRowCustomFieldApplies(context.row.original, customFieldId)) {
      return (
        <TableTooltip title={t`Hidden due to visibility settings`}>
          <CellContainer>
            <MutedText>{t`Not applicable`}</MutedText>
          </CellContainer>
        </TableTooltip>
      );
    }
    // flexRender renders the base cell as a component, preserving its hook order.
    return flexRender(baseCell, context);
  };

// ============================================================================
// Text / Description Cell
// ============================================================================

export type TextCellConfig = {
  placeholder?: string;
  maxChar?: number | null;
  editable?: boolean | ((cell: unknown) => boolean);
  onUpdate?: OnUpdate<{ id: number | string }>;
};

/**
 * Read-only text cell — truncated with tooltip.
 */
export const makeTextReadOnlyCell = <T extends { id: number | string }>() =>
  function TextReadOnlyCell(context: CellContext<T, unknown>): React.ReactNode {
    const value = String(context.getValue() ?? '');
    return (
      <TableTooltip title={value}>
        <CellContainer>
          <TruncatedText>{value}</TruncatedText>
        </CellContainer>
      </TableTooltip>
    );
  };

/**
 * Editable text cell — inline input via EditableTextCell.
 */
export const makeTextEditableCell = <T extends { id: number | string }>(config: {
  placeholder?: string;
  maxChar?: number | null;
  editable?: boolean | ((cell: unknown) => boolean);
  onUpdate?: (row: T, updates: Partial<T>) => void | Promise<void>;
  isMandatory?: boolean;
  entity?: HierarchyChildBulkEditEntity;
}) =>
  function TextEditableCell(context: CellContext<T, unknown>): React.ReactNode {
    const editableProp = config.editable;
    const isRowEditable =
      typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row may carry readyForExport
    const row = context.row.original as T & { readyForExport?: boolean };
    const isRequired = config.isMandatory === true && row.readyForExport === true;

    const validate = useCallback(
      (value: string): string | undefined => {
        if (isRequired && (!value || value.trim() === '')) {
          return getMandatoryForExportMessage(config.entity);
        }
        return undefined;
      },
      [isRequired, config.entity]
    );

    return (
      <EditableTextCell
        context={context}
        editable={isRowEditable}
        placeholder={config.placeholder}
        maxChars={config.maxChar ?? undefined}
        validate={isRequired ? validate : undefined}
        onUpdate={newValue =>
          config.onUpdate?.(
            context.row.original,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            { [context.column.id]: newValue } as Partial<T>
          )
        }
      />
    );
  };

// ============================================================================
// Number Cell
// ============================================================================

/**
 * Read-only number cell.
 */
export const makeNumberReadOnlyCell = <T extends { id: number | string }>() =>
  function NumberReadOnlyCell(context: CellContext<T, unknown>): React.ReactNode {
    const value = context.getValue();
    const title = String(value);
    return (
      <CellContainer>
        {value != null ? (
          <Tooltip title={title}>
            <span>{title}</span>
          </Tooltip>
        ) : (
          ''
        )}
      </CellContainer>
    );
  };

/**
 * Editable number cell — inline EditableNumberCell that updates the customFields array.
 * Mirrors makeTextEditableCell / makeDateEditableCell so numeric custom fields are
 * editable inline on the Styles and Items tables (dl-60391 / dl-59137).
 */
export const makeNumberEditableCell = <T extends { id: number | string }>(config: {
  editable?: boolean | ((cell: unknown) => boolean);
  onUpdate?: (row: T, updates: Partial<T>) => void | Promise<void>;
  allowDecimals?: boolean;
  isMandatory?: boolean;
  entity?: HierarchyChildBulkEditEntity;
  allowedDecimals?: number;
  maxChar?: number | null;
}) =>
  function NumberEditableCell(context: CellContext<T, unknown>): React.ReactNode {
    const editableProp = config.editable;
    const isRowEditable =
      typeof editableProp === 'function' ? editableProp(context) : editableProp === true;
    const value = context.getValue();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- CF numeric value is string|number|null
    const numericValue = (value as string | number | null | undefined) ?? null;
    const colId = context.column.id;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row may carry readyForExport
    const row = context.row.original as T & { readyForExport?: boolean };
    const isRequired = config.isMandatory === true && row.readyForExport === true;

    // EditableNumberCell renders a non-interactive read view (no `_` placeholder)
    // when editable={false}, so non-editable rows are handled here too — no need
    // to duplicate makeNumberReadOnlyCell (mirrors makeTextEditableCell).
    return (
      <EditableNumberCell<T>
        context={context}
        value={numericValue}
        editable={isRowEditable}
        allowDecimals={config.allowDecimals ?? false}
        validate={
          isRequired
            ? newValue =>
                newValue == null ? getMandatoryForExportMessage(config.entity) : undefined
            : undefined
        }
        allowedDecimals={config.allowedDecimals}
        maxChar={config.maxChar}
        onUpdate={newValue =>
          config.onUpdate?.(
            context.row.original,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- CF column id is the update key
            { [colId]: newValue } as Partial<T>
          )
        }
      />
    );
  };

// ============================================================================
// Date Cell
// ============================================================================

/**
 * Read-only date cell — formatted with date-fns.
 */
export const makeDateReadOnlyCell = <T extends { id: number | string }>() =>
  function DateReadOnlyCell(context: CellContext<T, unknown>): React.ReactNode {
    const value = context.getValue();
    const formatted = defaultDateFormatter(value as string | null);
    const tooltipText = value
      ? (() => {
          try {
            return format(new Date(value as string), 'dd-MMM-yyyy');
          } catch {
            return String(value);
          }
        })()
      : '';
    return (
      <TableTooltip title={tooltipText}>
        <CellContainer>
          <span>{formatted}</span>
        </CellContainer>
      </TableTooltip>
    );
  };

/**
 * Editable date cell — inline DatePickerCell that updates the customFields array.
 */
export const makeDateEditableCell = <T extends { id: number | string }>(config: {
  editable?: boolean | ((cell: unknown) => boolean);
  onUpdate?: (row: T, updates: Partial<T>) => void | Promise<void>;
  isMandatory?: boolean;
  entity?: HierarchyChildBulkEditEntity;
}) =>
  function DateEditableCell(context: CellContext<T, unknown>): React.ReactNode {
    const editableProp = config.editable;
    const isRowEditable =
      typeof editableProp === 'function' ? editableProp(context) : editableProp === true;
    const value = context.getValue();
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- cell value is string for date CFs
    const dateString = (value as string | null) ?? null;

    // Self-managed edit state (same pattern as makeAllowedValueDropdownCell)
    const [isEditing, setIsEditing] = useState(false);
    const editableCtx = useEditableContextOptional();
    const setEditableState = editableCtx?.setEditableState;
    usePropDrivenInlineEditSync(context.row.index, context.column.id, setIsEditing);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row may carry readyForExport
    const row = context.row.original as T & { readyForExport?: boolean };
    const isRequired = config.isMandatory === true && row.readyForExport === true;

    if (!isRowEditable) {
      const formatted = defaultDateFormatter(dateString);
      const tooltipText = dateString
        ? (() => {
            try {
              return format(new Date(dateString), 'dd-MMM-yyyy');
            } catch {
              return String(dateString);
            }
          })()
        : '';
      return (
        <TableTooltip title={tooltipText}>
          <CellContainer>
            <span>{formatted}</span>
          </CellContainer>
        </TableTooltip>
      );
    }

    const colId = context.column.id;

    return (
      <DatePickerCell<T>
        context={context}
        value={dateString}
        required={isRequired}
        requiredMessage={getMandatoryForExportMessage(config.entity)}
        editableRowIndex={isEditing ? context.row.index : null}
        editableColumnId={isEditing ? context.column.id : null}
        onCellEdit={() => {
          setIsEditing(true);
          setEditableState?.({ rowIndex: context.row.index, columnId: context.column.id });
        }}
        onClickAway={() => {
          setIsEditing(false);
          setEditableState?.({ rowIndex: null, columnId: null });
        }}
        onUpdate={newDate => {
          if (!newDate) {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- CF column id is the update key
            config.onUpdate?.(context.row.original, { [colId]: null } as Partial<T>);
            setIsEditing(false);
            setEditableState?.({ rowIndex: null, columnId: null });
            return;
          }
          const parsed = getDateFromValue(newDate);
          if (!parsed?.isValid()) return;
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- CF column id is the update key
          config.onUpdate?.(context.row.original, { [colId]: newDate } as Partial<T>);
          setIsEditing(false);
          setEditableState?.({ rowIndex: null, columnId: null });
        }}
      />
    );
  };

// ============================================================================
// Read-only AllowedValue dropdown cell — displays value as a bordered Tag.
// ============================================================================

export const makeAllowedValueDropdownReadOnlyCell = <T extends { id: number | string }>(config: {
  options?: ComboBoxOption[];
  placeholder?: string;
}) =>
  function AllowedValueDropdownReadOnlyCell(context: CellContext<T, unknown>): React.ReactNode {
    const cellValue = getStringValue(context);

    if (!cellValue) {
      return <CellContainer> </CellContainer>;
    }

    const isValid = isValueValid(cellValue, config.options);
    const TagComponent = isValid ? Tag : InvalidTag;
    const tooltipText = isValid ? cellValue : `${cellValue} (invalid value)`;

    return (
      <TableTooltip title={tooltipText}>
        <CellContainer>
          <TagsContainer>
            <TagComponent>{cellValue}</TagComponent>
          </TagsContainer>
        </CellContainer>
      </TableTooltip>
    );
  };

// ============================================================================
// AllowedValue (single-select, non-nested) Cell — uses shared EditableDropdownCell
// for visual + interaction parity with the general-field inline-edit dropdowns
// (Brand / Brand contact / Season / Group / Supplier on the Styles table).
// ============================================================================

export const makeAllowedValueDropdownCell = <T extends { id: number | string }>(config: {
  options?: ComboBoxOption[];
  editable?: boolean | ((cell: unknown) => boolean);
  isMandatory?: boolean;
  entity?: HierarchyChildBulkEditEntity;
}) =>
  function AllowedValueDropdownCell(context: CellContext<T, unknown>): React.ReactNode {
    // Self-managed edit state: the legacy Table used by Styles does not provide
    // an EditableContext, so EditableDropdownCell's context-based isEditing path
    // is inert there. Driving it via props keeps the cell working under both the
    // legacy Table and OptimizedTable.
    const [isEditing, setIsEditing] = useState(false);
    const editableCtx = useEditableContextOptional();
    const setEditableState = editableCtx?.setEditableState;
    usePropDrivenInlineEditSync(context.row.index, context.column.id, setIsEditing);

    const cellValue = getStringValue(context);
    const isRowEditable =
      typeof config.editable === 'function' ? config.editable(context) : config.editable === true;

    if (!isRowEditable) {
      return <ReadOnlyDropdownCell value={cellValue} showAvatar={false} />;
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row may carry readyForExport
    const row = context.row.original as T & { readyForExport?: boolean };
    const isRequired = config.isMandatory === true && row.readyForExport === true;

    const isValid = isValueValid(cellValue, config.options);
    const errorMessage = getInvalidAllowedValueError(cellValue, isValid);
    const colId = context.column.id;
    const customFieldId = parseCustomFieldColumnId(colId);

    return (
      <EditableDropdownCell<T>
        context={context}
        options={config.options ?? []}
        value={cellValue}
        showAvatar={false}
        required={isRequired}
        requiredMessage={getMandatoryForExportMessage(config.entity)}
        error={!!errorMessage}
        tooltipTitle={errorMessage}
        editableRowIndex={isEditing ? context.row.index : null}
        editableColumnId={isEditing ? context.column.id : null}
        onCellEdit={() => {
          setIsEditing(true);
          setEditableState?.({ rowIndex: context.row.index, columnId: context.column.id });
        }}
        onClickAway={() => {
          setIsEditing(false);
          setEditableState?.({ rowIndex: null, columnId: null });
        }}
        onChange={(_e, selected) => {
          setIsEditing(false);
          setEditableState?.({ rowIndex: null, columnId: null });
          if (customFieldId === null) return;
          if (selected === null) {
            // Clear the CF value while preserving sibling CF entries on the row.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const rowWithCfs = context.row.original as unknown as {
              customFields?: CustomFieldEntry[];
            };
            const existing = rowWithCfs.customFields ?? [];
            const hadExisting = existing.some(cf => cf.customFieldId === customFieldId);
            if (!hadExisting) return;
            const clearedEntry: CustomFieldEntry = {
              ...(existing.find(cf => cf.customFieldId === customFieldId) ?? {
                id: 0,
                customFieldId,
                customFieldAllowedValueUserDefinedID: null,
              }),
              customFieldId,
              value: null,
              itemCustomFieldAllowedValueId: null,
            };
            const nextCustomFields = existing.map(cf =>
              cf.customFieldId === customFieldId ? clearedEntry : cf
            );
            context.table.options.meta?.updateData?.(context.row.index, {
              customFields: nextCustomFields,
            });
            return;
          }
          if (!selected || typeof selected === 'string' || Array.isArray(selected)) return;
          const selectedNumericId =
            typeof selected.id === 'number' ? selected.id : Number(selected.id);
          if (!Number.isFinite(selectedNumericId)) return;
          const selectedLabel = selected.label ?? '';
          // Preserve other CF values on the row — the row's customFields array is
          // the full set of values; replacing it with just our entry would wipe
          // siblings out of the local cache and the accessorFn would render blank.
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row.original is the generic T; we know callers (Style, Item) carry customFields entries
          const rowWithCfs = context.row.original as unknown as {
            customFields?: CustomFieldEntry[];
          };
          const existing = rowWithCfs.customFields ?? [];
          const hadExisting = existing.some(cf => cf.customFieldId === customFieldId);
          const updatedEntry: CustomFieldEntry = {
            ...(existing.find(cf => cf.customFieldId === customFieldId) ?? {
              id: 0,
              customFieldId,
              customFieldAllowedValueUserDefinedID: null,
            }),
            customFieldId,
            value: selectedLabel,
            itemCustomFieldAllowedValueId: selectedNumericId,
          };
          const nextCustomFields = hadExisting
            ? existing.map(cf => (cf.customFieldId === customFieldId ? updatedEntry : cf))
            : [...existing, updatedEntry];
          context.table.options.meta?.updateData?.(context.row.index, {
            customFields: nextCustomFields,
          });
        }}
      />
    );
  };

// ============================================================================
// NestedAllowedValue (single-select with cross-field hierarchy) Cell
// Uses the same EditableDropdownCell UI as makeAllowedValueDropdownCell, with
// option filtering based on the parent field's current selection and a
// confirmation dialog when changing the parent would clear a child value.
// ============================================================================

export type NestedAllowedValueCellConfig = {
  options?: ComboBoxOption[];
  /** Parent custom field ID — options are filtered by the parent's selected value */
  parentCustomFieldId?: number;
  /** Whether to apply within-field hierarchy filtering (mother field) */
  isNested?: boolean;
  /** IDs of descendant custom fields to cascade-clear when this field changes */
  childCustomFieldIds?: number[];
  /** Map of descendant cfId → display name, for confirmation dialog copy */
  childCustomFieldNames?: Record<number, string>;
  /** Map of descendant cfId → isMandatory, to detect a mandatory-for-export child being cleared (dl-60826) */
  childCustomFieldMandatory?: Record<number, boolean>;
  /** Display name of this field's parent field, for "Select [name] first" placeholder */
  motherFieldName?: string;
  editable?: boolean | ((cell: unknown) => boolean);
  isMandatory?: boolean;
  entity?: HierarchyChildBulkEditEntity;
};

export const makeNestedAllowedValueDropdownCell = <T extends { id: number | string }>(
  config: NestedAllowedValueCellConfig
) =>
  function NestedAllowedValueDropdownCell(context: CellContext<T, unknown>): React.ReactNode {
    const [isEditing, setIsEditing] = useState(false);
    const editableCtx = useEditableContextOptional();
    const setEditableState = editableCtx?.setEditableState;
    usePropDrivenInlineEditSync(context.row.index, context.column.id, setIsEditing);
    const { openDialog } = useDialog();

    const cellValue = getStringValue(context);
    const isRowEditable =
      typeof config.editable === 'function' ? config.editable(context) : config.editable === true;

    if (!isRowEditable) {
      return <ReadOnlyDropdownCell value={cellValue} showAvatar={false} />;
    }

    const colId = context.column.id;
    const customFieldId = parseCustomFieldColumnId(colId);

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const rowWithCfs = context.row.original as unknown as { customFields?: CustomFieldEntry[] };
    const existing = rowWithCfs.customFields ?? [];

    // Compute filtered options based on cross-field or within-field hierarchy
    let filteredOptions = config.options ?? [];

    if (config.parentCustomFieldId) {
      const parentEntry = existing.find(cf => cf.customFieldId === config.parentCustomFieldId);
      const parentSelectedId = parentEntry?.itemCustomFieldAllowedValueId;
      if (parentSelectedId) {
        filteredOptions = filteredOptions.filter(
          opt => opt.customProps?.parentId === parentSelectedId
        );
      } else {
        // Parent has no value — show non-interactive placeholder
        const parentName = config.motherFieldName || 'parent';
        const title = t`Select ${parentName} first`;
        return (
          <CellContainer>
            <Tooltip title={title}>
              <TruncatedText style={{ color: 'inherit', opacity: 0.5 }}>{title}</TruncatedText>
            </Tooltip>
          </CellContainer>
        );
      }
    } else if (config.isNested) {
      // Mother field: filter within-field hierarchy based on current selection
      const currentEntry = existing.find(cf => cf.customFieldId === customFieldId);
      const currentValueId = currentEntry?.itemCustomFieldAllowedValueId ?? null;
      filteredOptions = filterOptionsForNested(filteredOptions, currentValueId);
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row may carry readyForExport
    const rowNested = context.row.original as T & { readyForExport?: boolean };
    const isRequiredNested = config.isMandatory === true && rowNested.readyForExport === true;

    const isValid = isValueValid(cellValue, filteredOptions);
    const errorMessage = getInvalidAllowedValueError(cellValue, isValid);

    const handleChange = async (
      _e: React.SyntheticEvent,
      selected: ComboBoxOption | ComboBoxOption[] | null | string
    ): Promise<void> => {
      // Close the dropdown visually immediately, but keep editableState armed so
      // the nav guard stays active during any subsequent async confirmation dialogs
      // (openDialog is awaited below). editableState is cleared at every exit path.
      setIsEditing(false);
      if (customFieldId === null) {
        setEditableState?.({ rowIndex: null, columnId: null });
        return;
      }

      // "Set empty" — clear this field and all descendants
      if (selected === null) {
        const descendantsWithValues = (config.childCustomFieldIds ?? []).filter(cfId => {
          const entry = existing.find(cf => cf.customFieldId === cfId);
          return (
            entry?.itemCustomFieldAllowedValueId != null ||
            (entry?.value != null && entry.value !== '')
          );
        });

        // Every descendant is cascade-cleared below regardless of whether it already
        // held a value (dl-60826), so ANY mandatory-for-export descendant — including
        // one being set for the first time and left empty — ends up empty after this
        // save. While the row is Ready for Export that would otherwise be rejected
        // outright; cascade Ready for Export to No in the same save instead of leaving
        // the user stuck. Both Styles and Items backends honor this cascade.
        const mandatoryDescendantIds = (config.childCustomFieldIds ?? []).filter(
          cfId => config.childCustomFieldMandatory?.[cfId]
        );
        const willClearReadyForExport =
          rowNested.readyForExport === true && mandatoryDescendantIds.length > 0;

        if (descendantsWithValues.length > 0 || willClearReadyForExport) {
          const childNames = descendantsWithValues
            .map(cfId => config.childCustomFieldNames?.[cfId] ?? String(cfId))
            .join(', ');
          const mandatoryChildNames = mandatoryDescendantIds
            .map(cfId => config.childCustomFieldNames?.[cfId] ?? String(cfId))
            .join(', ');
          const result = await openDialog({
            id: `cf-hierarchy-clear-${customFieldId}`,
            title:
              descendantsWithValues.length > 0
                ? t`Clear ${childNames}?`
                : t`Set Ready for Export to No?`,
            description:
              descendantsWithValues.length > 0
                ? willClearReadyForExport
                  ? config.entity === 'style'
                    ? t`Setting this field to empty will also remove the current value in ${childNames}. Since ${mandatoryChildNames} is required for Ready for Export, this style will also be set to Ready for Export → No.`
                    : t`Setting this field to empty will also remove the current value in ${childNames}. Since ${mandatoryChildNames} is required for Ready for Export, this item will also be set to Ready for Export → No.`
                  : t`Setting this field to empty will also remove the current value in ${childNames}.`
                : config.entity === 'style'
                  ? t`${mandatoryChildNames} is required for Ready for Export and will remain empty. This style will be set to Ready for Export → No.`
                  : t`${mandatoryChildNames} is required for Ready for Export and will remain empty. This item will be set to Ready for Export → No.`,
            actions: [
              { id: 'cancel', label: t`Cancel`, variant: 'secondary', size: 'lg' },
              { id: 'continue', label: t`Continue`, variant: 'primary', size: 'lg' },
            ],
            hideDontShowAgain: true,
          });
          if (result !== 'continue') {
            setEditableState?.({ rowIndex: null, columnId: null });
            return;
          }
        }

        const touchedIds = new Set([customFieldId, ...(config.childCustomFieldIds ?? [])]);
        const kept = existing.filter(cf => !touchedIds.has(cf.customFieldId));
        const existingEntry = existing.find(cf => cf.customFieldId === customFieldId);
        const clearedSelf: CustomFieldEntry = {
          ...(existingEntry ?? {
            id: 0,
            customFieldId,
            customFieldAllowedValueUserDefinedID: null,
          }),
          customFieldId,
          value: null,
          itemCustomFieldAllowedValueId: null,
        };
        const clearedDescendants: CustomFieldEntry[] = (config.childCustomFieldIds ?? []).map(
          cfId => {
            const prev = existing.find(cf => cf.customFieldId === cfId);
            return {
              ...(prev ?? {
                id: 0,
                customFieldId: cfId,
                customFieldAllowedValueUserDefinedID: null,
              }),
              customFieldId: cfId,
              value: null,
              itemCustomFieldAllowedValueId: null,
            };
          }
        );
        context.table.options.meta?.updateData?.(context.row.index, {
          customFields: [...kept, clearedSelf, ...clearedDescendants],
          ...(willClearReadyForExport ? { readyForExport: false } : {}),
        });
        setEditableState?.({ rowIndex: null, columnId: null });
        return;
      }

      if (typeof selected === 'string' || Array.isArray(selected)) {
        setEditableState?.({ rowIndex: null, columnId: null });
        return;
      }

      const selectedNumericId = typeof selected.id === 'number' ? selected.id : Number(selected.id);
      if (!Number.isFinite(selectedNumericId)) {
        setEditableState?.({ rowIndex: null, columnId: null });
        return;
      }

      const existingEntry = existing.find(cf => cf.customFieldId === customFieldId);

      // No-op when the same value is re-selected: nothing changes, so don't prompt to
      // clear children or write an update (dl-60387).
      if (existingEntry?.itemCustomFieldAllowedValueId === selectedNumericId) {
        setEditableState?.({ rowIndex: null, columnId: null });
        return;
      }

      const updatedEntry: CustomFieldEntry = {
        ...(existingEntry ?? { id: 0, customFieldId, customFieldAllowedValueUserDefinedID: null }),
        customFieldId,
        value: selected.label ?? null,
        itemCustomFieldAllowedValueId: selectedNumericId,
      };

      // Find descendants with current values that would be cleared
      const descendantsWithValues = (config.childCustomFieldIds ?? []).filter(cfId => {
        const entry = existing.find(cf => cf.customFieldId === cfId);
        return (
          entry?.itemCustomFieldAllowedValueId != null ||
          (entry?.value != null && entry.value !== '')
        );
      });

      // Every descendant is cascade-cleared below regardless of whether it already
      // held a value (dl-60826), so ANY mandatory-for-export descendant — including
      // one being set for the first time and left empty — ends up empty after this
      // save. While the row is Ready for Export that would otherwise be rejected
      // outright; cascade Ready for Export to No in the same save instead of leaving
      // the user stuck. Both Styles and Items backends honor this cascade.
      const mandatoryDescendantIds = (config.childCustomFieldIds ?? []).filter(
        cfId => config.childCustomFieldMandatory?.[cfId]
      );
      const willClearReadyForExport =
        rowNested.readyForExport === true && mandatoryDescendantIds.length > 0;

      // Confirmation dialog when a descendant would be cleared, or when a mandatory
      // descendant (with or without a prior value) will remain empty.
      if (descendantsWithValues.length > 0 || willClearReadyForExport) {
        const childNames = descendantsWithValues
          .map(cfId => config.childCustomFieldNames?.[cfId] ?? String(cfId))
          .join(', ');
        const mandatoryChildNames = mandatoryDescendantIds
          .map(cfId => config.childCustomFieldNames?.[cfId] ?? String(cfId))
          .join(', ');
        const fieldName = config.motherFieldName ?? 'this field';
        const result = await openDialog({
          id: `cf-hierarchy-clear-${customFieldId}`,
          title:
            descendantsWithValues.length > 0
              ? t`Clear ${childNames}?`
              : t`Set Ready for Export to No?`,
          description:
            descendantsWithValues.length > 0
              ? willClearReadyForExport
                ? config.entity === 'style'
                  ? t`Changing ${fieldName} will remove the current value in ${childNames}. Since ${mandatoryChildNames} is required for Ready for Export, this style will also be set to Ready for Export → No.`
                  : t`Changing ${fieldName} will remove the current value in ${childNames}. Since ${mandatoryChildNames} is required for Ready for Export, this item will also be set to Ready for Export → No.`
                : t`Changing ${fieldName} will remove the current value in ${childNames}.`
              : config.entity === 'style'
                ? t`${mandatoryChildNames} is required for Ready for Export and will remain empty. This style will be set to Ready for Export → No.`
                : t`${mandatoryChildNames} is required for Ready for Export and will remain empty. This item will be set to Ready for Export → No.`,
          actions: [
            { id: 'cancel', label: t`Cancel`, variant: 'secondary', size: 'lg' },
            { id: 'continue', label: t`Continue`, variant: 'primary', size: 'lg' },
          ],
          hideDontShowAgain: true,
        });
        if (result !== 'continue') {
          setEditableState?.({ rowIndex: null, columnId: null });
          return;
        }
      }

      // Build next customFields: update this field + clear all descendants
      const touchedIds = new Set([customFieldId, ...(config.childCustomFieldIds ?? [])]);
      const kept = existing.filter(cf => !touchedIds.has(cf.customFieldId));
      const cleared: CustomFieldEntry[] = (config.childCustomFieldIds ?? []).map(cfId => {
        const prev = existing.find(cf => cf.customFieldId === cfId);
        return {
          ...(prev ?? { id: 0, customFieldId: cfId, customFieldAllowedValueUserDefinedID: null }),
          customFieldId: cfId,
          value: null,
          itemCustomFieldAllowedValueId: null,
        };
      });
      const nextCustomFields: CustomFieldEntry[] = [...kept, updatedEntry, ...cleared];

      // delegate to meta.updateData
      context.table.options.meta?.updateData?.(context.row.index, {
        customFields: nextCustomFields,
        ...(willClearReadyForExport ? { readyForExport: false } : {}),
      });
      setEditableState?.({ rowIndex: null, columnId: null });
    };

    return (
      <EditableDropdownCell<T>
        context={context}
        options={filteredOptions}
        value={cellValue}
        showAvatar={false}
        required={isRequiredNested}
        requiredMessage={getMandatoryForExportMessage(config.entity)}
        error={!!errorMessage}
        tooltipTitle={errorMessage}
        editableRowIndex={isEditing ? context.row.index : null}
        editableColumnId={isEditing ? context.column.id : null}
        onCellEdit={() => {
          setIsEditing(true);
          setEditableState?.({ rowIndex: context.row.index, columnId: context.column.id });
        }}
        onClickAway={() => {
          setIsEditing(false);
          setEditableState?.({ rowIndex: null, columnId: null });
        }}
        onChange={handleChange}
      />
    );
  };

// ============================================================================
// Multiselect Cell
// ============================================================================

export type MultiselectCellConfig<T extends { id: number | string }> = {
  options?: ComboBoxOption[];
  editable?: boolean | ((cell: unknown) => boolean);
  onUpdate?: OnUpdate<T>;
  columnLabel?: string;
  isMandatory?: boolean;
  entity?: HierarchyChildBulkEditEntity;
};

/**
 * Read-only multiselect cell — displays values as TagsWithOverflow.
 */
export const makeMultiselectReadOnlyCell = <T extends { id: number | string }>() =>
  function MultiselectReadOnlyCell(context: CellContext<T, unknown>): React.ReactNode {
    const value = getCellValue(context, context.column.id);
    const values = parseMultiSelectValue(value).sort((a, b) => a.label.localeCompare(b.label));

    if (values.length === 0) {
      return <TagsReadOnlyContainer> </TagsReadOnlyContainer>;
    }

    return (
      <TagsReadOnlyContainer>
        <TagsWithOverflow values={values} columnWidth={context.column.getSize()} />
      </TagsReadOnlyContainer>
    );
  };

/**
 * Editable multiselect cell — uses EditableMultiselectCell from shared components.
 * Falls back to read-only when no options are available.
 */
export const makeMultiselectEditableCell = <T extends { id: number | string }>(
  config: MultiselectCellConfig<T>
) =>
  function MultiselectEditableCell(context: CellContext<T, unknown>): React.ReactNode {
    const value = getCellValue(context, context.column.id);

    const values = parseMultiSelectValue(value).sort((a, b) => a.label.localeCompare(b.label));
    const editableProp = config.editable;
    const isRowEditable =
      typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

    const colWidth = context.column.getSize() - 24;
    const readOnlyDisplay = (
      <TagsReadOnlyContainer>
        {values.length === 0 ? null : <TagsWithOverflow values={values} columnWidth={colWidth} />}
      </TagsReadOnlyContainer>
    );

    if (!isRowEditable || !config.options || config.options.length === 0) {
      return readOnlyDisplay;
    }

    const currentValues: ComboBoxOption[] = values.map(v => ({ id: v.id, label: v.label }));

    if (!config.onUpdate) return readOnlyDisplay;

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- row may carry readyForExport
    const row = context.row.original as T & { readyForExport?: boolean };
    const isRequired = config.isMandatory === true && row.readyForExport === true;

    const errorMessage = getInvalidMultiselectError(currentValues, config.options);

    return (
      <EditableMultiselectCellWithInvalidValueGuard<T>
        context={context}
        value={currentValues}
        options={config.options}
        onUpdate={config.onUpdate}
        columnId={context.column.id}
        columnLabel={config.columnLabel ?? ''}
        required={isRequired}
        requiredMessage={getMandatoryForExportMessage(config.entity)}
        errorMessage={errorMessage}
      />
    );
  };

/**
 * Wraps EditableMultiselectCell so a selection that includes a value no longer among
 * the field's active allowed options shows a tooltip error on hover — mirrors the
 * single-select and nested allowed-value dropdowns above. Exit semantics:
 * - Unchanged selection (even if it holds a stale value): Esc / click-away exit freely,
 *   no API call.
 * - Changed selection that is still invalid: click-away / Enter toast and keep the cell
 *   open (via `validateSelection`); Esc still cancels and reverts to the initial value.
 * - Changed selection that is now valid: saves on exit.
 */
const EditableMultiselectCellWithInvalidValueGuard = <T extends { id: number | string }>({
  errorMessage,
  ...rest
}: EditableMultiselectCellProps<T> & { errorMessage?: string }) => {
  const editableCtx = useEditableContextOptional();
  const setEditableState = editableCtx?.setEditableState;

  return (
    <EditableMultiselectCell<T>
      {...rest}
      error={!!errorMessage}
      tooltipTitle={errorMessage}
      // Block committing a CHANGED selection that still contains a deactivated allowed
      // value (toast + stay in edit). An unchanged selection exits freely; Esc always
      // cancels and reverts. See the `validateSelection` prop docs.
      validateSelection={values => getInvalidMultiselectError(values, rest.options)}
      onClickAway={() => {
        setEditableState?.({ rowIndex: null, columnId: null });
      }}
    />
  );
};
